import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Target, Calendar, Trophy, MapPin } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";

interface Mission {
  id: string;
  title: string;
  description: string;
  missionType: string;
  targetEmojiPin?: string;
  targetLocation?: {
    lat: number;
    lng: number;
    radius: number;
  };
  rewardTokens: number;
  rewardNft?: string;
  difficulty: string;
  status: string;
  maxCompletions?: number;
  currentCompletions: number;
  expiresAt?: string;
}

interface LegalWall {
  id: string;
  name: string;
  city: string;
  country: string;
  address: string;
  latitude: number;
  longitude: number;
  description: string;
  hours: string;
  permitRequired: boolean;
}

export default function Missions() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<'missions' | 'legal-walls'>('missions');

  const { data: missions = [], isLoading: missionsLoading } = useQuery({
    queryKey: ["/api/missions"],
  });

  const { data: legalWalls = [], isLoading: wallsLoading } = useQuery({
    queryKey: ["/api/legal-walls"],
  });

  const filteredMissions = (missions as Mission[]).filter((mission: Mission) =>
    mission.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mission.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredWalls = (legalWalls as LegalWall[]).filter((wall: LegalWall) =>
    wall.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wall.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isLoading = missionsLoading || wallsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-neon-green border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white">Loading Missions & Legal Walls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-base text-white">
      {/* Header */}
      <header className="bg-dark-elevated border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-neon-green"
              data-testid="button-back"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center space-x-2">
              <Target className="text-coral" size={24} />
              <h1 className="text-xl font-bold">Missions & Legal Walls</h1>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            {activeTab === 'missions' ? `${(missions as Mission[]).length} Active Missions` : `${(legalWalls as LegalWall[]).length} Legal Walls`}
          </div>
        </div>
      </header>

      <div className="p-6 max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="mb-6 flex space-x-1 bg-dark-elevated rounded-lg p-1">
          <Button
            variant={activeTab === 'missions' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('missions')}
            className={`flex-1 ${activeTab === 'missions' ? 'bg-neon-green text-black' : 'text-gray-300'}`}
            data-testid="tab-missions"
          >
            <Target className="mr-2" size={16} />
            Street Art Missions
          </Button>
          <Button
            variant={activeTab === 'legal-walls' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('legal-walls')}
            className={`flex-1 ${activeTab === 'legal-walls' ? 'bg-neon-green text-black' : 'text-gray-300'}`}
            data-testid="tab-legal-walls"
          >
            <MapPin className="mr-2" size={16} />
            🧱 Legal Graffiti Walls
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder={activeTab === 'missions' ? "Search missions..." : "Search legal walls..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-dark-elevated border-gray-600 text-white"
              data-testid="input-search"
            />
          </div>
        </div>

        {/* Missions Tab */}
        {activeTab === 'missions' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMissions.map((mission: Mission) => (
              <Card 
                key={mission.id}
                className="bg-dark-elevated border-gray-600 hover:border-neon-green transition-colors"
                data-testid={`mission-card-${mission.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-bold text-white">
                      {mission.targetEmojiPin} {mission.title}
                    </CardTitle>
                    <Badge 
                      className={`${
                        mission.difficulty === 'easy' ? 'bg-green-600' :
                        mission.difficulty === 'medium' ? 'bg-yellow-600' :
                        'bg-red-600'
                      } text-white`}
                    >
                      {mission.difficulty.charAt(0).toUpperCase() + mission.difficulty.slice(1)}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {mission.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-coral">
                      <Trophy size={16} />
                      <span className="text-sm font-medium">{mission.rewardTokens} 🪙</span>
                    </div>
                    <Badge variant="outline" className="border-neon-green text-neon-green">
                      {mission.missionType}
                    </Badge>
                  </div>

                  {mission.maxCompletions && (
                    <div className="text-xs text-gray-400">
                      Progress: {mission.currentCompletions}/{mission.maxCompletions} completions
                    </div>
                  )}

                  {mission.expiresAt && (
                    <div className="flex items-center space-x-1 text-yellow-400 text-xs">
                      <Calendar size={12} />
                      <span>Expires: {new Date(mission.expiresAt).toLocaleDateString()}</span>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-600">
                    <Button
                      size="sm"
                      onClick={() => {
                        if (mission.targetLocation) {
                          setLocation(`/?lat=${mission.targetLocation.lat}&lng=${mission.targetLocation.lng}&zoom=16`);
                        } else {
                          setLocation('/');
                        }
                      }}
                      className="w-full bg-neon-green text-black hover:bg-neon-green/90"
                      data-testid={`button-start-mission-${mission.id}`}
                    >
                      <Target size={16} className="mr-2" />
                      Start Mission
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Legal Walls Tab */}
        {activeTab === 'legal-walls' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWalls.map((wall: LegalWall) => (
              <Card 
                key={wall.id}
                className="bg-dark-elevated border-gray-600 hover:border-neon-green transition-colors"
                data-testid={`wall-card-${wall.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-bold text-white">
                      🧱 {wall.name}
                    </CardTitle>
                    {!wall.permitRequired && (
                      <Badge className="bg-green-600 text-white">
                        No Permit Required
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {wall.description}
                  </p>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin size={14} className="text-neon-green" />
                      <span className="text-gray-300">{wall.city}, {wall.country}</span>
                    </div>
                    
                    <div className="text-gray-400 text-xs">
                      <strong>Address:</strong> {wall.address}
                    </div>
                    
                    <div className="text-gray-400 text-xs">
                      <strong>Hours:</strong> {wall.hours}
                    </div>
                  </div>

                  {wall.permitRequired && (
                    <Badge variant="outline" className="border-yellow-400 text-yellow-400">
                      Permit Required
                    </Badge>
                  )}

                  <div className="pt-3 border-t border-gray-600">
                    <Button
                      size="sm"
                      onClick={() => setLocation(`/?lat=${wall.latitude}&lng=${wall.longitude}&zoom=16`)}
                      className="w-full bg-neon-green text-black hover:bg-neon-green/90"
                      data-testid={`button-view-wall-${wall.id}`}
                    >
                      <MapPin size={16} className="mr-2" />
                      View on Map
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {((activeTab === 'missions' && filteredMissions.length === 0) || 
          (activeTab === 'legal-walls' && filteredWalls.length === 0)) && (
          <div className="text-center py-12">
            {activeTab === 'missions' ? (
              <Target className="mx-auto text-gray-500 mb-4" size={48} />
            ) : (
              <MapPin className="mx-auto text-gray-500 mb-4" size={48} />
            )}
            <h3 className="text-lg font-semibold mb-2">
              No {activeTab === 'missions' ? 'Missions' : 'Legal Walls'} Found
            </h3>
            <p className="text-gray-400">
              Try adjusting your search terms
            </p>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <Card className="bg-dark-elevated border-gray-600 max-w-3xl mx-auto">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-3 text-neon-green">
                {activeTab === 'missions' ? 'About Missions' : 'About Legal Walls'}
              </h3>
              <p className="text-gray-300 mb-4">
                {activeTab === 'missions' ? 
                  'Complete street art missions to earn tokens, unlock achievements, and discover hidden gems in your city. Each mission offers unique challenges and rewards.' :
                  'Legal graffiti walls are designated spaces where street artists can practice their craft legally. Always check local regulations and respect community guidelines.'
                }
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}